import React, { useCallback, useEffect, useRef, useState } from 'react';

interface Equipment { id: number; code: string; name: string; isActive: boolean }
interface ProcessItem { id: number; code: string; name: string }
interface Link { equipmentId: number; processId: number; rateKgPerHour: number | null }
type CoverageStatus = 'OK' | 'NO_PROCESS_MATCH' | 'AMBIGUOUS_MATCH' | 'NO_EQUIPMENT' | 'NO_MACHINE_NEEDED';
interface CoverageRow {
  routeProcess: string;
  steps: number;
  processId: number | null;
  processName: string | null;
  equipmentCount: number;
  status: CoverageStatus;
}
interface CoverageSummary { total: number; ok: number; noMachine: number; noProcessMatch: number; ambiguous: number; noEquipment: number }

// processId -> rate text ('' = no rate). A key present means the process is enabled.
type Selection = Record<number, string>;

const STATUS_STYLES: Record<CoverageStatus, { label: string; cls: string; hint: string }> = {
  OK: { label: 'Cubierto', cls: 'bg-green-100 text-green-700', hint: '' },
  NO_PROCESS_MATCH: {
    label: 'Sin proceso en catálogo',
    cls: 'bg-red-100 text-red-700',
    hint:
      'Si es un proceso real, créalo en Catálogos → Procesos de Fabricación con ese nombre o con ese texto como código. Si el nombre está mal, corrígelo en Comercia: aquí se sobrescribe al sincronizar. No crees un proceso por cada máquina.',
  },
  AMBIGUOUS_MATCH: {
    label: 'Proceso ambiguo',
    cls: 'bg-amber-100 text-amber-700',
    hint: 'Hay más de un proceso del catálogo con ese nombre. Deja uno solo.',
  },
  NO_EQUIPMENT: {
    label: 'Sin equipos asignados',
    cls: 'bg-amber-100 text-amber-700',
    hint: 'El proceso existe, pero ningún equipo activo lo tiene habilitado. Si este proceso no usa máquina, márcalo en «Procesos que no usan máquina».',
  },
  NO_MACHINE_NEEDED: {
    label: 'No requiere máquina',
    cls: 'bg-gray-100 text-gray-600',
    hint: '',
  },
};

const jsonHeaders = { 'Content-Type': 'application/json' };

async function readError(res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => ({}));
  return body?.detail || body?.error || fallback;
}

const toSelection = (links: Link[], equipmentId: number): Selection => {
  const sel: Selection = {};
  for (const l of links) {
    if (l.equipmentId === equipmentId) sel[l.processId] = l.rateKgPerHour == null ? '' : String(l.rateKgPerHour);
  }
  return sel;
};

const sameSelection = (a: Selection, b: Selection) => {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => k in b && a[Number(k)] === b[Number(k)]);
};

const EquipmentProcessMatrix: React.FC = () => {
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [processes, setProcesses] = useState<ProcessItem[]>([]);
  const [links, setLinks] = useState<Link[]>([]);
  const [coverage, setCoverage] = useState<{ rows: CoverageRow[]; summary: CoverageSummary } | null>(null);
  const [drafts, setDrafts] = useState<Record<number, Selection>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [noMachineIds, setNoMachineIds] = useState<number[]>([]);
  const [noMachineDraft, setNoMachineDraft] = useState<number[] | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<{ id: number; message: string } | null>(null);
  const loadRef = useRef<Promise<void> | null>(null);
  const requestRef = useRef(0);

  const load = useCallback(() => {
    // One load at a time (React StrictMode runs the first effect twice).
    if (loadRef.current) return loadRef.current;
    const req = ++requestRef.current;
    const run = (async () => {
      setError(null);
      try {
        const [cfg, cov] = await Promise.all([
          fetch('/api/planning/equipment-processes', { credentials: 'include' }),
          fetch('/api/planning/process-coverage', { credentials: 'include' }),
        ]);
        if (!cfg.ok) throw new Error(await readError(cfg, 'Error al cargar los procesos por equipo'));
        if (!cov.ok) throw new Error(await readError(cov, 'Error al cargar la cobertura de procesos'));
        const cfgData = await cfg.json();
        const covData = await cov.json();
        if (requestRef.current === req) {
          setEquipment(cfgData.equipment);
          setProcesses(cfgData.processes);
          setLinks(cfgData.links);
          setNoMachineIds(Array.isArray(cfgData.noMachineProcessIds) ? cfgData.noMachineProcessIds : []);
          setCoverage(covData);
        }
      } catch (e) {
        if (requestRef.current === req) setError(e instanceof Error ? e.message : 'Error al cargar');
      } finally {
        if (requestRef.current === req) setLoading(false);
        loadRef.current = null;
      }
    })();
    loadRef.current = run;
    return run;
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectionOf = (equipmentId: number): Selection => drafts[equipmentId] ?? toSelection(links, equipmentId);

  const toggle = (equipmentId: number, processId: number) => {
    const sel = { ...selectionOf(equipmentId) };
    if (processId in sel) delete sel[processId];
    else sel[processId] = '';
    setDrafts((d) => ({ ...d, [equipmentId]: sel }));
  };

  const setRate = (equipmentId: number, processId: number, text: string) => {
    setDrafts((d) => ({ ...d, [equipmentId]: { ...selectionOf(equipmentId), [processId]: text } }));
  };

  const save = async (equipmentId: number) => {
    const sel = selectionOf(equipmentId);
    const payload: Array<{ processId: number; rateKgPerHour: number | null }> = [];
    for (const key of Object.keys(sel)) {
      const processId = Number(key);
      const text = sel[processId].trim();
      let rate: number | null = null;
      if (text.includes(',')) {
        setSaveError({ id: equipmentId, message: 'Escribe el rendimiento sin comas; usa punto para decimales (ej. 1200.5).' });
        return;
      }
      if (text !== '') {
        rate = Number(text);
        if (!Number.isFinite(rate) || rate <= 0) {
          setSaveError({ id: equipmentId, message: 'El rendimiento debe ser un número mayor a 0 (kg/h).' });
          return;
        }
        if (rate > 1_000_000) {
          setSaveError({ id: equipmentId, message: 'El rendimiento no puede superar 1,000,000 kg/h.' });
          return;
        }
      }
      payload.push({ processId, rateKgPerHour: rate });
    }
    setSavingId(equipmentId);
    setSaveError(null);
    try {
      const res = await fetch(`/api/planning/equipment-processes/${equipmentId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify({ processes: payload }),
      });
      if (!res.ok) throw new Error(await readError(res, 'Error al guardar'));
      const saved = await res.json();
      const returned: Link[] = Array.isArray(saved?.links) ? saved.links : [];
      setLinks((prev) => [...prev.filter((l) => l.equipmentId !== equipmentId), ...returned]);
      setDrafts((d) => {
        const next = { ...d };
        delete next[equipmentId];
        return next;
      });
      await load();
    } catch (e) {
      setSaveError({ id: equipmentId, message: e instanceof Error ? e.message : 'Error al guardar' });
    } finally {
      setSavingId(null);
    }
  };

  const busy = savingId !== null || savingSettings;

  const currentNoMachine = noMachineDraft ?? noMachineIds;
  const settingsDirty =
    noMachineDraft !== null &&
    (noMachineDraft.length !== noMachineIds.length || noMachineDraft.some((id) => !noMachineIds.includes(id)));

  const toggleNoMachine = (processId: number) => {
    const base = currentNoMachine;
    setNoMachineDraft(base.includes(processId) ? base.filter((id) => id !== processId) : [...base, processId]);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    setSettingsError(null);
    try {
      const res = await fetch('/api/planning/process-settings', {
        method: 'PUT',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify({ noMachineProcessIds: currentNoMachine }),
      });
      if (!res.ok) throw new Error(await readError(res, 'Error al guardar'));
      const saved = await res.json();
      setNoMachineIds(Array.isArray(saved.noMachineProcessIds) ? saved.noMachineProcessIds : currentNoMachine);
      setNoMachineDraft(null);
      await load();
    } catch (e) {
      setSettingsError(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) return <p className="text-gray-500 text-sm">Cargando procesos por equipo...</p>;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-5">
      <div>
        <h2 className="text-lg font-bold text-gray-800">Procesos por equipo</h2>
        <p className="text-sm text-gray-600 mt-1">
          Marca qué procesos de la ruta de fabricación puede hacer cada máquina. El rendimiento en kg/h es opcional. Los procesos que
          no usan máquina (por ejemplo, una espera) se marcan aparte y no aparecen en las tarjetas de las máquinas.
        </p>
        <p className="text-sm text-gray-600 mt-1">
          Por ahora esta pantalla solo registra y reporta: todavía NO restringe al planeador actual (el Tablero de Planeación, la
          Lista Maestra y «Optimizar con IA» funcionan exactamente igual que antes). El futuro motor determinista usará estos
          vínculos como regla dura (por ejemplo, extrusión solo en extrusoras).
        </p>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>}

      {coverage && (
        <div>
          <h3 className="font-semibold text-gray-700 text-sm mb-2">
            Cobertura de las órdenes por planear
            <span className="font-normal text-gray-500">
              {' '}— {coverage.summary.ok + coverage.summary.noMachine} de {coverage.summary.total} proceso(s) listos
            </span>
          </h3>
          {coverage.rows.length === 0 ? (
            <p className="text-sm text-gray-500">No hay órdenes autorizadas con pasos de ruta.</p>
          ) : (
            <div className="border border-gray-200 rounded-xl overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-left">
                  <tr>
                    {['Proceso en las órdenes', 'Pasos pendientes', 'Proceso del catálogo', 'Equipos', 'Estado'].map((h) => (
                      <th key={h} className="px-3 py-2 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {coverage.rows.map((r) => {
                    const st = STATUS_STYLES[r.status];
                    return (
                      <tr key={r.routeProcess}>
                        <td className="px-3 py-2">{r.routeProcess}</td>
                        <td className="px-3 py-2">{r.steps}</td>
                        <td className="px-3 py-2">{r.processName ?? '—'}</td>
                        <td className="px-3 py-2">{r.equipmentCount}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.cls}`}>{st.label}</span>
                          {(() => {
                            const hint =
                              r.status === 'NO_PROCESS_MATCH' && r.routeProcess === '(sin proceso)'
                                ? 'Hay pasos de ruta sin nombre de proceso; revisa la importación de esas órdenes.'
                                : st.hint;
                            return hint ? <p className="text-xs text-gray-500 mt-1 max-w-md">{hint}</p> : null;
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {processes.length > 0 && (
        <div className="border border-gray-200 rounded-xl p-4">
          <h3 className="font-semibold text-gray-700 text-sm">Procesos que no usan máquina</h3>
          <p className="text-xs text-gray-500 mt-1 mb-3">
            Marca los procesos que no se hacen en una máquina. Quedan como «No requiere máquina» en el reporte y dejan de aparecer
            en las tarjetas. Si ya tenían máquinas marcadas, esos vínculos se conservan, pero se ignoran.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {processes.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm min-w-0">
                <input
                  type="checkbox"
                  checked={currentNoMachine.includes(p.id)}
                  disabled={busy}
                  onChange={() => toggleNoMachine(p.id)}
                />
                <span className="truncate" title={`${p.code} — ${p.name}`}>{p.code} — {p.name}</span>
              </label>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button
              disabled={!settingsDirty || busy}
              onClick={saveSettings}
              className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm disabled:opacity-50"
            >
              {savingSettings ? 'Guardando...' : 'Guardar'}
            </button>
            {settingsError && <span className="text-sm text-red-700">{settingsError}</span>}
          </div>
        </div>
      )}

      {error && processes.length === 0 && equipment.length === 0 ? null : processes.length === 0 ? (
        <p className="text-sm text-gray-500">
          No hay procesos de fabricación en el catálogo. Créalos primero en Catálogos → Procesos de Fabricación.
        </p>
      ) : equipment.length === 0 ? (
        <p className="text-sm text-gray-500">No hay equipos en el catálogo. Créalos en Catálogos → Equipos.</p>
      ) : (
        <div className="space-y-3">
          {equipment.map((eq) => {
            const sel = selectionOf(eq.id);
            const dirty = !sameSelection(sel, toSelection(links, eq.id));
            const saving = savingId === eq.id;
            return (
              <div key={eq.id} className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div className="font-medium text-gray-800">
                    {eq.code} — {eq.name}
                    {!eq.isActive && <span className="ml-2 text-xs text-gray-500">(inactivo)</span>}
                  </div>
                  <button
                    disabled={!dirty || busy}
                    onClick={() => save(eq.id)}
                    className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm disabled:opacity-50"
                  >
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {processes.filter((p) => !noMachineIds.includes(p.id)).map((p) => {
                    const enabled = p.id in sel;
                    return (
                      <div key={p.id} className="flex items-center gap-2 text-sm">
                        <label className="flex items-center gap-2 flex-1 min-w-0">
                          <input
                            type="checkbox"
                            checked={enabled}
                            disabled={busy}
                            onChange={() => toggle(eq.id, p.id)}
                          />
                          <span className="truncate" title={`${p.code} — ${p.name}`}>{p.code} — {p.name}</span>
                        </label>
                        {enabled && (
                          <input
                            value={sel[p.id]}
                            disabled={busy}
                            onChange={(e) => setRate(eq.id, p.id, e.target.value)}
                            placeholder="kg/h, ej. 1200.5"
                            inputMode="decimal"
                            aria-label={`Rendimiento de ${eq.name} en ${p.name}`}
                            className="w-28 border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                {saveError && saveError.id === eq.id && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-2 text-red-700 text-sm">{saveError.message}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EquipmentProcessMatrix;
